export interface VarDef {
	/** Variable name used in templates, e.g. "stationName" */
	n: string;
	/** Human-friendly display label, e.g. "Station name" */
	d: string;
	/** Sample value shown in sample-mode, e.g. "WhaleLO Station A" */
	s: string;
}

const PAYOUT_VARS: VarDef[] = [
	{ n: "stationName", d: "Station name", s: "WhaleLO Station A" },
	{ n: "displayName", d: "Display name", s: "Station A" },
	{ n: "requestCount", d: "Request count", s: "12" },
	{ n: "fromDate", d: "From date", s: "Mar 1, 2026" },
	{ n: "toDate", d: "To date", s: "Mar 15, 2026" },
	{ n: "totalAmount", d: "Total amount", s: "8,300,000" },
	{ n: "currencyCode", d: "Currency", s: "VND" },
	{ n: "payoutWindow", d: "Payout window", s: "Apr 1–5, 2026" },
	{ n: "paymentDate", d: "Payment date", s: "Apr 2026" },
	{ n: "approvedAmount", d: "Approved amount", s: "8,300,000" },
	{ n: "requestDates", d: "Request date(s)", s: "10 Mar 2026" },
	{ n: "requestDate", d: "Request date", s: "10 Mar 2026" },
	{ n: "requestAmount", d: "Requested amount", s: "8,300,000" },
	{ n: "payoutAccount", d: "Payout account", s: "BIDV ••••7559" },
	{ n: "payoutPeriod", d: "Scheduled payout period", s: "1–5 Apr 2026" },
	{ n: "rejectReason", d: "Reject reason", s: "Bank account name does not match" },
];

export const VARSET: Record<string, VarDef[]> = {
	booking: [
		{ n: "orderId", d: "Booking ID", s: "100245" },
		{ n: "customerName", d: "Customer name", s: "Minh Anh" },
		{ n: "customerFullPhone", d: "Customer phone", s: "+84 912 345 678" },
		{ n: "customerEmail", d: "Customer email", s: "minh.anh@gmail.com" },
		{ n: "quantity", d: "Luggage count", s: "3" },
		{ n: "total", d: "Total", s: "45.00" },
		{ n: "dropOffDate", d: "Drop-off date", s: "12 Jun 2026" },
		{ n: "pickUpDate", d: "Pick-up date", s: "15 Jun 2026" },
		{ n: "formattedTimeIn", d: "Drop-off time", s: "10:00 12 Jun 2026" },
		{ n: "formattedTimeOut", d: "Pick-up time", s: "18:00 13 Jun 2026" },
		{ n: "displayName", d: "Station (display)", s: "The Coffee House — Bui Vien" },
		{ n: "facilityHotline1", d: "Hotline", s: "+84 866 888 431" },
		{ n: "partnerName", d: "Station name", s: "Hotel ABC" },
		{ n: "partnerAddress", d: "Station address", s: "68 Bui Vien Street, District 1, HCMC" },
		{ n: "mapLink", d: "Map link", s: "Open in Google Maps" },
	],
	finance: PAYOUT_VARS,
	station: [
		{ n: "ownerName", d: "Owner name", s: "Tran Van Binh" },
		{ n: "businessName", d: "Business name", s: "Hotel ABC" },
		{ n: "email", d: "Email", s: "abc@gmail.com" },
		{ n: "password", d: "Password", s: "Wh@le2026" },
		{ n: "loginUrl", d: "Login link", s: "partner.whalelo.net/login" },
		{ n: "reason", d: "Rejection reason", s: "Missing business license" },
		{ n: "reviewUrl", d: "Review link", s: "admin.whalelo.net/review/1024" },
		{ n: "formId", d: "Form ID", s: "1024" },
	],
};

/**
 * Returns the variable catalog for a given group key ("booking" | "finance" | "station").
 * Falls back to the finance/payout set when the group is unknown.
 */
export function getVarCatalog(group: string): VarDef[] {
	return VARSET[group] ?? PAYOUT_VARS;
}

/** Minimal shape of a backend email keyword (subset of services EmailKeyword). */
export interface KeywordLike {
	name: string;
	description?: string;
	descriptionManual?: string;
	enabled?: boolean;
}

/**
 * Friendly English label + sample value for EVERY backend keyword, so the palette
 * reads cleanly instead of showing raw / full-sentence descriptions. Keyed by
 * bare variable name. Used by mergeCatalog for keywords not already in the curated set.
 */
const KEYWORD_DICT: Record<string, { d: string; s: string }> = {
	name: { d: "Customer name", s: "Minh Anh" },
	orderId: { d: "Booking ID", s: "100245" },
	transactionId: { d: "Transaction ID", s: "TXN-558210" },
	email: { d: "Customer email", s: "minh.anh@gmail.com" },
	quantity: { d: "Luggage count", s: "3" },
	total: { d: "Total", s: "45.00" },
	timeIn: { d: "Drop-off time (raw)", s: "2026-06-12 10:00" },
	timeOut: { d: "Pick-up time (raw)", s: "2026-06-13 18:00" },
	formattedTimeIn: { d: "Drop-off time", s: "10:00 12 Jun 2026" },
	formattedTimeOut: { d: "Pick-up time", s: "18:00 13 Jun 2026" },
	dropOffDate: { d: "Drop-off date", s: "12 Jun 2026" },
	pickUpDate: { d: "Pick-up date", s: "15 Jun 2026" },
	displayName: { d: "Station (display)", s: "Station A" },
	stationName: { d: "Station name (legal)", s: "WhaleLO Station A" },
	partnerAddress: { d: "Station address", s: "68 Bui Vien Street, District 1, HCMC" },
	partnerImages: { d: "Station images", s: "[station photos]" },
	addressText: { d: "Address (text)", s: "68 Bui Vien Street, District 1" },
	partnerHotlineNumber: { d: "Station hotline", s: "+84 866 888 431" },
	facilityHotline1: { d: "Hotline 1", s: "+84 866 888 431" },
	facilityHotline2: { d: "Hotline 2", s: "+84 866 888 432" },
	requestTimeUtc: { d: "Request time (UTC)", s: "03:00 UTC" },
	requestDateUtc: { d: "Request date (UTC)", s: "Apr 2, 2026" },
	requestAmount: { d: "Request amount", s: "8,300,000" },
	currencyCode: { d: "Currency", s: "VND" },
	paymentDate: { d: "Payment date", s: "Apr 2026" },
	requestCount: { d: "Request count", s: "12" },
	fromDate: { d: "From date", s: "Mar 1, 2026" },
	toDate: { d: "To date", s: "Mar 15, 2026" },
	totalAmount: { d: "Total amount", s: "8,300,000" },
	stationOpenTime: { d: "Opening time", s: "08:00" },
	stationCloseTime: { d: "Closing time", s: "22:00" },
	customerName: { d: "Customer name", s: "Minh Anh" },
	customerEmail: { d: "Customer email", s: "minh.anh@gmail.com" },
	customerPhone: { d: "Customer phone", s: "912 345 678" },
	customerPhonePrefix: { d: "Phone country code", s: "+84" },
	customerFullPhone: { d: "Full phone", s: "+84 912 345 678" },
	mapLink: { d: "Map link", s: "Open in Google Maps" },
	password: { d: "Login password", s: "Wh@le2026" },
	verificationPin: { d: "Verification PIN", s: "1234" },
	ownerName: { d: "Station owner", s: "Tran Van Binh" },
	businessName: { d: "Business name", s: "Hotel ABC" },
	loginUrl: { d: "Login link", s: "partner.whalelo.net/login" },
	reason: { d: "Rejection reason", s: "Missing business license" },
	formId: { d: "Form ID", s: "1024" },
	businessType: { d: "Business type", s: "Hotel" },
	ownerPhone: { d: "Owner phone", s: "+84 912 345 678" },
	verificationMethod: { d: "Verification method", s: "Email" },
	reviewUrl: { d: "Review link", s: "admin.whalelo.net/review/1024" },
};

/**
 * Merge the curated catalog for `group` with the FULL backend keyword list so every
 * `${...}` the old email format supported is available in the builder. Curated entries
 * win (nicer labels + sample values); backend keywords fill in everything else.
 */
export function mergeCatalog(group: string, keywords?: KeywordLike[]): VarDef[] {
	const base = getVarCatalog(group);
	if (!keywords || keywords.length === 0) return base;
	const seen = new Set(base.map((v) => v.n));
	const extras: VarDef[] = [];
	for (const kw of keywords) {
		if (kw.enabled === false) continue;
		const bare = String(kw.name || "")
			.replace(/^\$\{(.+)\}$/, "$1")
			.trim();
		if (!bare || seen.has(bare)) continue;
		seen.add(bare);
		const dict = KEYWORD_DICT[bare];
		const label = dict ? dict.d : (kw.descriptionManual || kw.description || bare).trim();
		extras.push({ n: bare, d: label, s: dict ? dict.s : "" });
	}
	return [...base, ...extras];
}

/**
 * Returns the human-friendly label for a variable name, or the name itself if not found.
 */
export function varLabel(catalog: VarDef[], name: string): string {
	const found = catalog.find((v) => v.n === name);
	return found ? found.d : name;
}

/**
 * Returns the sample value for a variable name, or the name itself if not found.
 */
export function varSample(catalog: VarDef[], name: string): string {
	const found = catalog.find((v) => v.n === name);
	return found ? found.s || found.d : name;
}
